import {
  Button,
  Col, Input,
  Pagination,
  Popover,
  Row,
  Select,
  Skeleton, Table,
  DatePicker
} from "antd";
import { useEffect, useState, useRef } from "react";
import { FaEye, FaSearch, FaTrash } from "react-icons/fa";
import { MdTune } from "react-icons/md";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Get } from "../../config/api/get";
import { Delete } from "../../config/api/delete";
import { BOOKING, FARE, RATES } from "../../config/constants/api";
import { useDebouncedCallback } from "use-debounce";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import Swal from "sweetalert2";

const ReportManagement = () => {
  dayjs.extend(timezone);
  dayjs.extend(utc);
  const location = useLocation();
  const userId = location?.state?.userId;
  const chauffeurId = location?.state?.chauffeurId;
  const token = useSelector((state) => state.user.userToken);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const [bookings, setBookings] = useState(null);
  const [latestRates, setLatestRates] = useState(null);
  const [latestDistanceRates, setLatestDistanceRates] = useState(null);
  const [latestFares, setLatestFares] = useState(null);
  const [paginationConfig, setPaginationConfig] = useState({
    pageNumber: 1,
    limit: 10,
    totalDocs: 0,
    totalPages: 0,
  });
  const [filter, setFilter] = useState({
    status: null,
    serviceType: null,
    keyword: "",
    from: null,
    to: null,
  });
  
  // Ref to store latest filter for debounced callback
  const filterRef = useRef(filter);
  
  // Update ref whenever filter changes
  useEffect(() => {
    filterRef.current = filter;
  }, [filter]);
  
  // Unified function to fetch bookings with all filters
  const getUsers = (pageNumber, pageSize, filters = null) => {
    setLoading(true);
    const activeFilters = filters || filter;
    
    // Format dates if they exist
    let fromDate = null;
    let toDate = null;
    if (activeFilters.from) {
      // dayjs() can handle dayjs objects, Date objects, strings, etc.
      fromDate = dayjs(activeFilters.from).format("YYYY-MM-DD");
    }
    if (activeFilters.to) {
      // dayjs() can handle dayjs objects, Date objects, strings, etc.
      toDate = dayjs(activeFilters.to).format("YYYY-MM-DD");
    }
    
    // Build query parameters
    const queryParams = {
      user: userId,
      chauffeur: chauffeurId,
      page: pageNumber
        ? pageNumber.toString()
        : paginationConfig.pageNumber.toString(),
      limit: pageSize ? pageSize.toString() : paginationConfig.limit.toString(),
    };
    
    // Add optional filters only if they have values
    if (activeFilters.keyword) {
      queryParams.keyword = activeFilters.keyword;
    }
    if (activeFilters.status) {
      queryParams.status = activeFilters.status;
    }
    if (activeFilters.serviceType) {
      queryParams.serviceType = activeFilters.serviceType;
    }
    if (fromDate) {
      queryParams.from = fromDate;
    }
    if (toDate) {
      queryParams.to = toDate;
    }
    
    Get(BOOKING.getAllBookings, token, queryParams)
      .then((response) => {
        if (response?.data?.docs) {
          setBookings(response?.data?.docs);
          setPaginationConfig({
            pageNumber: response?.data?.page,
            limit: response?.data?.limit,
            totalDocs: response?.data?.totalDocs,
            totalPages: response?.data?.totalPages,
          });
          setLoading(false);
        }
      })
      .catch((err) => {
        console.log("Error Fetching Loads", err);
        setLoading(false);
      });
  };
  useEffect(() => {
    getUsers();
    getAllLatestRates();
    getAllLatestFares();
    getAllLatestDistanceRates();
    // eslint-disable-next-line
  }, []);
  const handlePageChange = (pageNumber) => {
    setPaginationConfig({
      ...paginationConfig,
      pageNumber: pageNumber,
    });
    // Preserve all active filters when changing page
    getUsers(pageNumber, paginationConfig.limit, filter);
  };

  // Debounced function for API call - uses ref to get latest filter state
  const debouncedSearch = useDebouncedCallback((searchValue) => {
    // Get the latest filter state from ref (updated by useEffect)
    const currentFilter = filterRef.current;
    const updatedFilter = {
      ...currentFilter,
      keyword: searchValue,
    };
    // Apply search with all existing filters
    getUsers(paginationConfig.pageNumber, paginationConfig.limit, updatedFilter);
  }, 1000);

  const handleSearch = (value) => {
    // Update filter state immediately for responsive UI
    const updatedFilter = {
      ...filter,
      keyword: value,
    };
    setFilter(updatedFilter);
    // Trigger debounced API call with the search value
    // The ref will be updated by useEffect, so the debounced function will use latest filter
    debouncedSearch(value);
  };
  const startIndex =
    (paginationConfig.pageNumber - 1) * paginationConfig.limit + 1;
  const endIndex = Math.min(
    startIndex + paginationConfig.limit - 1,
    paginationConfig.totalDocs
  );
  const message = bookings
    ? `Showing records ${endIndex} of ${paginationConfig.totalDocs}`
    : "Showing records 0 of 0";
  const handleOpenChange = (newOpen) => {
    setOpen(newOpen);
  };
  
  const handleFrom = (date) => {
    setFilter({
      ...filter,
      from: date ? date : null,
    });
  };

  const handleTo = (date) => {
    setFilter({
      ...filter,
      to: date ? date : null,
    });
  };
  
  const handleApply = () => {
    // Apply all filters together (status, serviceType, dates) with existing keyword
    getUsers(paginationConfig.pageNumber, paginationConfig.limit, filter);
    setOpen(false); // Close the filter popover after applying
  };
  
  const handleClear = () => {
    const clearedFilter = {
      status: null,
      serviceType: null,
      keyword: "", // Clear search keyword too
      from: null,
      to: null,
    };
    setFilter(clearedFilter);
    getUsers(1, paginationConfig.limit, clearedFilter);
    setOpen(false); // Close the filter popover after clearing
  };
  
  const handleStatusChange = (value) => {
    setFilter({
      ...filter,
      status: value,
    });
  };
  
  const handleServiceTypeChange = (value) => {
    setFilter({
      ...filter,
      serviceType: value,
    });
  };

  const handleDelete = (bookingId, bookingStatus) => {
    Swal.fire({
      title: "Are you sure?",
      html: `
        <p>You are about to delete this booking.</p>
        ${bookingStatus === "ONGOING" ? '<p style="color: #ff9800;">Note: Ongoing bookings cannot be deleted.</p>' : ''}
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
      reverseButtons: true,
      focusConfirm: false,
      focusCancel: true,
    }).then((result) => {
      if (result.isConfirmed) {
        // Show loading state
        Swal.fire({
          title: "Deleting...",
          text: "Please wait while we delete the booking.",
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          },
        });

        Delete(BOOKING.deleteBooking + bookingId, token, null)
          .then((response) => {
            // Refresh the bookings list immediately after successful deletion
            // Use filterRef to get the latest filter state to avoid stale closure
            const currentFilter = filterRef.current;
            const currentPage = paginationConfig.pageNumber;
            const currentPageSize = paginationConfig.limit;
            
            if (response?.success || response?.status) {
              // Refresh the table immediately before showing success message
              getUsers(currentPage, currentPageSize, currentFilter);
              
              Swal.fire({
                title: "Deleted!",
                text: response?.message || "Booking deleted successfully.",
                icon: "success",
                confirmButtonColor: "#3085d6",
              });
            } else {
              Swal.fire({
                title: "Error!",
                text: response?.message || "Failed to delete booking.",
                icon: "error",
                confirmButtonColor: "#d33",
              });
            }
          })
          .catch((err) => {
            const errorMessage = err?.response?.data?.message || err?.message || "Failed to delete booking.";
            
            // Still try to refresh in case the deletion actually succeeded
            const currentFilter = filterRef.current;
            const currentPage = paginationConfig.pageNumber;
            const currentPageSize = paginationConfig.limit;
            getUsers(currentPage, currentPageSize, currentFilter);
            
            Swal.fire({
              title: "Error!",
              text: errorMessage,
              icon: "error",
              confirmButtonColor: "#d33",
            });
          });
      }
    });
  };

  const filterContent = (
    <div className="filterDropdown">
      <div>
        <p
          className="mainLabel"
          style={{ padding: "10px", fontSize: "18px", fontWeight: "bold" }}
        >
          Filter
        </p>
      </div>
      <hr style={{ marginBottom: 10 }} />

      <div className="filterDropdownBody">
        <p className="mainLabel">From Date:</p>
        <DatePicker
          size={"large"}
          className="filterSelectBox"
          value={filter.from ? dayjs(filter.from) : null}
          onChange={(date) => handleFrom(date)}
          style={{
            width: "100%",
            marginBottom: "0px",
            textAlign: "left",
          }}
          format="YYYY-MM-DD"
        />
        
        <p className="mainLabel">To Date:</p>
        <DatePicker
          size={"large"}
          className="filterSelectBox"
          value={filter.to ? dayjs(filter.to) : null}
          onChange={(date) => handleTo(date)}
          style={{
            width: "100%",
            marginBottom: "0px",
            textAlign: "left",
          }}
          format="YYYY-MM-DD"
        />

        <p className="mainLabel">Status:</p>
        <Select
          size={"large"}
          className="filterSelectBox"
          placeholder="Select Status"
          value={filter.status}
          onChange={(e) => handleStatusChange(e)}
          allowClear
          style={{
            width: "100%",
            marginBottom: "10px",
            textAlign: "left",
          }}
          options={[
            { value: "PENDING", label: "Pending" },
            { value: "UPCOMING", label: "Upcoming" },
            { value: "ONGOING", label: "Ongoing" },
            { value: "ARRIVED", label: "Arrived" },
            { value: "COMPLETED", label: "Completed" },
            { value: "PAID", label: "Paid" },
          ]}
        />
        
        <p className="mainLabel">Service Type:</p>
        <Select
          size={"large"}
          className="filterSelectBox"
          placeholder="Select Service Type"
          value={filter.serviceType}
          onChange={(e) => handleServiceTypeChange(e)}
          allowClear
          style={{
            width: "100%",
            marginBottom: "10px",
            textAlign: "left",
          }}
          options={[
            { value: "Black", label: "Black Luxury" },
            { value: "Ultra", label: "Ultra Luxury" },
            { value: "Ultra Beyond", label: "Ultra Beyond Luxury" },
            { value: "Suv", label: "SUV Luxury" },
            { value: "Sprinter", label: "Sprinter" },
          ]}
        />

        <Button
          type=""
          block
          size={"large"}
          style={{ marginBottom: "10px" }}
          className="web-btn"
          //   onClick={() => getUsers()}
          onClick={handleApply}
        >
          Apply
        </Button>
        <Button
          type=""
          block
          size={"large"}
          className="web-btn"
          onClick={handleClear}
        >
          Clear All
        </Button>
      </div>
    </div>
  );

  const itemRender = (_, type, originalElement) => {
    if (type === "prev") {
      return (
        <button type="button" className="pagination-link">
          Previous
        </button>
      );
    }
    if (type === "next") {
      return (
        <button type="button" className="pagination-link">
          Next
        </button>
      );
    }

    return originalElement;
  };

  const getAllLatestRates = () => {
    Get(RATES.getAllLatestRates, token)
      .then((response) => {
        if (response?.status) {
          setLatestRates(response?.data);
        }
      })
      .catch((err) => {
        console.log("error", err);
      });
  };
  const getAllLatestDistanceRates = () => {
    Get(RATES.getAllLatestRates, token, { isDistanceRate: true })
      .then((response) => {
        if (response?.status) {
          setLatestDistanceRates(response?.data);
        }
      })
      .catch((err) => {
        console.log("error", err);
      });
  };
  const getAllLatestFares = () => {
    Get(FARE.getAllLatestFares, token)
      .then((response) => {
        if (response?.status) {
          setLatestFares(response?.data?.latestFares);
        }
      })
      .catch((err) => {
        console.log("error", err);
      });
  };
  const getPerMileRate = (serviceType, passengerCapacity) => {
    if (latestFares && passengerCapacity) {
      let service = latestFares.find(
        (fare) =>
          fare._id.serviceType === serviceType &&
          fare._id.passengerCapacity === passengerCapacity
      );
      return service?.fare;
    }
  };
  const getBookingTypeLabel = (item) => {
    if (item?.type === "PtoP") return "Distance";
    if (item?.type === "Hourly") return "Hourly";
    if (item?.type === "Subscription") return "Subscription";
    if (item?.dropoffLocation) return "Distance";
    return "Hourly";
  };

  const getHourlyRate = (item) => {
    if (item?.dropoffLocation) {
      if (latestDistanceRates) {
        let rate = latestDistanceRates.find(
          (rate) =>
            rate._id.serviceType === item?.serviceType &&
            rate._id.passengerCapacity === item?.passengerCapacity
        );
        return rate?.rate;
      }
    } else {
      if (latestRates) {
        let rate = latestRates.find(
          (rate) =>
            rate._id.serviceType === item?.serviceType &&
            rate._id.passengerCapacity === item?.passengerCapacity
        );
        return rate?.rate;
      }
    }
  };
  const columns = [
    {
      title: (
        <p className="uppercase font-euclid text-black font-bold text-xs text-center">
          S.NO
        </p>
      ),
      dataIndex: "key",
      key: "key",
      render: (value, item, index) => (
        <p className="text-xs text-center font-euclid text-[#575757]">
          {(index < 10 && "0") + (index + 1)}
        </p>
      ),
    },
    {
      title: (
        <p className="uppercase font-euclid text-black font-bold text-xs text-center">
          Date
        </p>
      ),
      dataIndex: "date",
      key: "date",
      render: (item) => <span>{dayjs.tz(item, "UTC").format("M/D/YYYY")}</span>,
    },
    {
      title: (
        <p className="uppercase font-euclid text-black font-bold text-xs text-center">
          Time
        </p>
      ),
      dataIndex: "time",
      key: "time",
      render: (item) => <span>{dayjs.tz(item, "UTC").format("hh:mm A")}</span>,
    },
    {
      title: (
        <p className="uppercase font-euclid text-black font-bold text-xs text-center">
          F. Name
        </p>
      ),
      dataIndex: ["user", "firstName"],
      key: "firstName",
      render: (item) => <span>{item ? item : "-"}</span>,
    },
    {
      title: (
        <p className="uppercase font-euclid text-black font-bold text-xs text-center">
          L. Name
        </p>
      ),
      dataIndex: ["user", "lastName"],
      key: "lastName",
      render: (item) => <span>{item ? item : "-"}</span>,
    },
    {
      title: (
        <p className="uppercase font-euclid text-black font-bold text-xs text-center">
          Pk-up Address
        </p>
      ),
      dataIndex: ["pickupLocation", "address"],
      key: ["pickupLocation", "address"],
      render: (item) => (
        <span>{item.length < 40 ? item : item.slice(0, 40) + "..."}</span>
      ),
    },
    {
      title: (
        <p className="uppercase font-euclid text-black font-bold text-xs text-center">
          Dr-off Address
        </p>
      ),
      dataIndex: ["dropoffLocation", "address"],
      key: ["dropoffLocation", "address"],
      render: (item) => (
        <span>
          {item
            ? item.length < 40
              ? item
              : item.slice(0, 40) + "..."
            : "Unknown"}
        </span>
      ),
    },
    {
      title: (
        <p className="uppercase font-euclid text-black font-bold text-xs text-center">
          Booking Type
        </p>
      ),
      dataIndex: "type",
      key: "bookingType",
      render: (value, item) => (
        <span>{getBookingTypeLabel(item)}</span>
      ),
    },
    {
      title: (
        <p className="uppercase font-euclid text-black font-bold text-xs text-center">
          Car Type
        </p>
      ),
      dataIndex: "serviceType",
      key: "serviceType",
      render: (value, item) => <span>{value + " Luxury"} </span>,
    },
    {
      title: (
        <p className="uppercase font-euclid text-black font-bold text-xs text-center">
          Req Duration
        </p>
      ),
      dataIndex: "duration",
      key: "duration",
      render: (value, item) => (
        <span>{value >= 60 ? value / 60 + " hours" : value + " mins"} </span>
      ),
    },
    {
      title: (
        <p className="uppercase font-euclid text-black font-bold text-xs text-center">
          Discount
        </p>
      ),
      dataIndex: "advanceBookingDiscount",
      key: "advanceBookingDiscount",
      render: (value, item) => (
        <span>
          {item.advanceBookingDiscount?.discountPercent 
            ? `${item.advanceBookingDiscount.discountPercent}%` 
            : "0%"}
        </span>
      ),
    },
    {
      title: (
        <p className="uppercase font-euclid text-black font-bold text-xs text-center">
          Rate
        </p>
      ),
      dataIndex: "",
      key: "rate",
      render: (item) =>
        item.dropoffLocation ? (
          <span>
            ${getPerMileRate(item?.serviceType, item?.passengerCapacity)} per
            mile
          </span>
        ) : (
          <span>${getHourlyRate(item)}Hr</span>
        ),
    },
    {
      title: (
        <p className="uppercase font-euclid text-black font-bold text-xs text-center">
          Amount
        </p>
      ),
      dataIndex: "amount",
      key: "amount",
      render: (value, item) => {
        let displayAmount = item?.initialCharges || 0;
        
        // If discount is applied, subtract discount amount from original amount
        if (item?.advanceBookingDiscount?.discountAmount) {
          displayAmount = item.initialCharges - item.advanceBookingDiscount.discountAmount;
        }
        
        return (
          <span>
            ${displayAmount.toFixed(2)}
            {!item.dropoffLocation && " + per mile + overtime"}
          </span>
        );
      },
    },
    // {
    //   title: (
    //     <p className="uppercase font-euclid text-black font-bold text-xs text-center">
    //       Per mile + overtime
    //     </p>
    //   ),
    //   dataIndex: "-",
    //   key: "-",
    //   render: (value, item) => (
    //     <span>
    //     $
    //     {getPerMileRate(item?.serviceType, item?.passengerCapacity)}
    //   </span>
    //   ),
    // },
    {
      title: (
        <p className="uppercase font-euclid text-black font-bold text-xs text-center">
          Status
        </p>
      ),
      dataIndex: "status",
      key: "status",
      render: (item) => (
        <>
          {item === "PENDING" ? (
            <span style={{ color: "#DD9F00" }}>{item}</span>
          ) : item === "PAID" ? (
            <span style={{ color: "#00D640" }}>{item}</span>
          ) : item === "UPCOMING" ? (
            <span style={{ color: "#2D308B" }}>{item}</span>
          ) : item === "ONGOING" ? (
            <span style={{ color: "#2D308B" }}>{item}</span>
          ) : item === "ARRIVED" ? (
            <span style={{ color: "#7c00ff" }}>{item}</span>
          ) : item === "COMPLETED" ? (
            <span style={{ color: "#00D640" }}>{item}</span>
          ) : (
            <span>{item}</span>
          )}
        </>
      ),
    },
    {
      title: (
        <p className="uppercase font-euclid text-black font-bold text-xs text-center">
          Booking No
        </p>
      ),
      dataIndex: "_id",
      key: "_id",
      render: (value, item) => "#" + value.slice(-4),
    },
    // {
    //   title: (
    //     <p className="uppercase font-euclid text-black font-bold text-xs text-center">
    //       booking id
    //     </p>
    //   ),
    //   dataIndex: "_id",
    //   key: "_id",
    //   render: (value, item, index) => (
    //     <p className="text-xs text-center font-euclid text-[#575757]">
    //       #{value.slice(-4)}
    //     </p>
    //   ),
    // },
    // {
    //   title: (
    //     <p className="uppercase font-euclid text-black font-bold text-xs text-center">
    //       Name
    //     </p>
    //   ),
    //   dataIndex: "customerName",
    //   key: "customerName",
    //   render: (value, item, index) => (
    //     <p className="text-xs text-center font-euclid text-[#575757]">
    //       {value}
    //     </p>
    //   ),
    // },
    // {
    //   title: (
    //     <p className="uppercase font-euclid text-black font-bold text-xs text-center">
    //       Email
    //     </p>
    //   ),
    //   dataIndex: ["user", "email"],
    //   key: ["user", "email"],
    //   render: (value, item, index) => (
    //     <p className="text-xs text-center font-euclid text-[#575757]">
    //       {value}
    //     </p>
    //   ),
    // },
    // {
    //   title: (
    //     <p className="uppercase font-euclid text-black font-bold text-xs text-center">
    //       Date Posted
    //     </p>
    //   ),
    //   dataIndex: "createdAt",
    //   key: "createdAt",
    //   render: (item) => <span>{dayjs(item).format("M/D/YYYY")}</span>,
    // },
    // {
    //   title: "Status",
    //   dataIndex: "status",
    //   key: "status",
    //   render: (item) => (
    //     <>
    //       {item === "UPCOMING" ? (
    //         <span style={{ color: "#DD9F00" }}>{item}</span>
    //       ) : item === "PENDING" ? (
    //         <span style={{ color: "#DD9F00" }}>{item}</span>
    //       ) : item === "ONGOING" ? (
    //         <span style={{ color: "#2D308B" }}>{item}</span>
    //       ) : item === "ARRIVED" ? (
    //         <span style={{ color: "#2D308B" }}>{item}</span>
    //       ) : item === "COMPLETED" ? (
    //         <span style={{ color: "#00D640" }}>{item}</span>
    //       ) : item === "PAID" ? (
    //         <span style={{ color: "#00D640" }}>{item}</span>
    //       ) : (
    //         <span>{item}</span>
    //       )}
    //     </>
    //   ),
    // },
    {
      title: (
        <p className="uppercase font-euclid text-black font-bold text-xs text-center">
          action
        </p>
      ),
      dataIndex: "_id",
      key: "_id",
      render: (value, item, index) => (
        <div className="flex justify-center items-center gap-2">
          <div className="flex justify-center bg-[#FDE6C3] rounded-[100%] w-fit p-2 mr-1">
            <FaEye
              className="text-[#CD810D] cursor-pointer h-[16px] w-[16px]"
              onClick={() => navigate("/reportDetails/" + item._id)}
            />
          </div>
          <div className="flex justify-center bg-[#FFE6E6] rounded-[100%] w-fit p-2">
            <FaTrash
              className="text-[#DC3545] cursor-pointer h-[16px] w-[16px]"
              onClick={() => handleDelete(item._id, item.status)}
            />
          </div>
        </div>
      ),
    },
  ];
  return (
    <Row justify="center">
      <Col md={23} xs={22}>
        <div className="flex max-md:flex-col max-md:items-start justify-between items-center my-8">
          <h3 className="font-tavi laptop:text-3xl text-xl font-normal text-black md:ml-8 max-md:mb-2">
            Recent Booking Management
          </h3>
          <div className="flex justify-center items-center max-md:w-full max-md:justify-end">
            <Input
              size="large"
              className="mainInput dashInput w-[700px] max-md:w-full max-md:max-w-full"
              placeholder="Search by name, email, or booking number"
              value={filter.keyword}
              onChange={(e) => handleSearch(e.target.value)}
              style={{ fontSize: "14px" }}
              suffix={
                <FaSearch
                  style={{
                    color: "#818181",
                    fontSize: 16,
                    cursor: "pointer",
                  }}
                />
              }
            />
            &emsp;
            <div className="flex justify-center">
              <Popover
                content={filterContent}
                trigger="click"
                open={open}
                onOpenChange={handleOpenChange}
                placement="bottomRight"
                arrow={false}
              >
                <Button
                  style={{
                    padding: "4px 11px",
                    height: "auto",
                    borderRadius: "6px",
                    backgroundColor: "white",
                  }}
                >
                  <MdTune className="text-[32px] text-[#767676]" />
                </Button>
              </Popover>
            </div>
          </div>
        </div>
        <div
          className="bg-white rounded-lg  py-8 my-8"
          style={{ boxShadow: "1px 1px 1px 1px rgba(0, 0, 0, 0.05)" }}
        >
          <Row style={{ overflow: "auto" }} className="px-6">
            {loading ? (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  width: "100%",
                }}
              >
                <Skeleton active />
                <br />
              </div>
            ) : (
              <Table
                className="styledTable"
                dataSource={bookings}
                columns={columns}
                pagination={false}
              />
            )}
          </Row>
          <Row style={{ padding: "10px 20px" }}>
            <Col xs={24} md={12}>
              <p className="font-euclid text-sm text-black">{message}</p>
            </Col>
            <Col
              xs={24}
              md={12}
              style={{ display: "flex", justifyContent: "flex-end" }}
            >
              <Pagination
                className="styledPagination"
                onChange={(e) => handlePageChange(e)}
                current={parseInt(paginationConfig.pageNumber)}
                pageSize={paginationConfig.limit}
                total={paginationConfig.totalDocs}
                itemRender={itemRender}
              />
            </Col>
          </Row>
        </div>
      </Col>
    </Row>
  );
};

export default ReportManagement;
